/* eslint-disable react-hooks/exhaustive-deps */
import {
  ChangeEvent,
  Key,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Button,
  Col,
  Dropdown,
  Form,
  InputGroup,
  Modal,
  Row,
  Table,
} from "react-bootstrap";
import { Calendar, DateObject } from "react-multi-date-picker";

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import CalenderIcon from "../../assets/icons/calendarIcon.svg";
import download from "../../assets/images/folder_download.svg";
import {
  agentListApi,
  auditFilterLogs,
  AuditLogT,
  auditTaskLogs,
} from "../../controllers/auditLogs";
import { orgListApi } from "../../controllers/organization";
import { taskListApi } from "../../controllers/task";
import { allUserList } from "../../controllers/users";
import { dateFormatter, timeFormatter } from "../common/dateFormat";
import Loader from "../common/Loader";
import NoDataFound from "../common/NoDataFound";
import { UserContext } from "../common/UserContext";
import {
  actionList,
  entityList,
  FilterDropdown,
  getChipStyle,
  getRole,
  getUserRole,
} from "./FilterDropDown";

interface FilterWarningModalProps {
  show: boolean;
  handleConfirm: () => void;
  handleCancel: () => void;
}

const FilterWarningModal: React.FC<FilterWarningModalProps> = ({
  show,
  handleConfirm,
  handleCancel,
}) => (
  <Modal
    show={show}
    onHide={handleCancel}
    centered
    size="lg"
    contentClassName="my-modal"
  >
    <Modal.Header closeButton>
      <Modal.Title>
        <h3 style={{ fontWeight: "bold" }}>Download Audit Logs</h3>
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <div className="pad-button">
        The download will be based on the filters you have applied. Do you want
        to proceed?
      </div>
    </Modal.Body>
    <Modal.Footer>
      <Button
        variant="primary"
        onClick={handleCancel}
        style={{
          width: "6rem",
          backgroundColor: "#2b339d",
        }}
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={handleConfirm}
        style={{
          width: "6rem",
          backgroundColor: "#2b339d",
        }}
      >
        Ok
      </Button>
    </Modal.Footer>
  </Modal>
);

function AuditLogs() {
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const observer = useRef<IntersectionObserver>();
  const { authToken } = useContext(UserContext);
  const [auditData, setAuditData] = useState<AuditLogT[]>([]);
  const [orgList, setOrgList] = useState<
    { _id: string; name: string; flag: any }[]
  >([]);
  const [userListItem, setUserListItem] = useState<any[]>([]);
  const [taskList, setTaskList] = useState<{ _id: string; name: string }[]>([]);
  const [agentList, setAgentList] = useState<{ _id: string; name: string }[]>(
    []
  );
  const [userOption, setUserOption] = useState<string[]>([]);
  const [orgOption, setOrgOption] = useState<string[]>([]);
  const [entityOption, setEntityOption] = useState<string[]>([]);
  const [actionOption, setActionOption] = useState<string[]>([]);
  const [filterResult, setFilterResult] = useState<AuditLogT[]>(auditData);
  const [scheduleDate, setScheduleDate] = useState<string[]>([]);
  const [showCalender, setShowCalender] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const sortByNewest = (a: AuditLogT, b: AuditLogT) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  };

  const lastLogElementRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setLoadingMore(true);
          setPageNumber((prevPageNumber) => prevPageNumber + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  const formatSelectedDays = (days: Array<string>) => {
    let selectedDays;
    if (days.length === 1) {
      selectedDays = days;
    } else if (days.length === 2) {
      selectedDays = days.join(" and ");
    } else {
      selectedDays = days.join(", ");
    }
    return selectedDays;
  };

  const formatSelectedTime = (time: string) => {
    const selectedTime = time.split(" ");
    return selectedTime[1].concat(":", selectedTime[0]);
  };

  const getAgentStatus = (status: boolean) => {
    let agentStatus;
    if (status) {
      agentStatus = "Activated";
    } else {
      agentStatus = "Deactivated";
    }
    return agentStatus;
  };

  const getOrgName = (orgId: string[] | string) => {
    let organizationNames: string[] = [];

    orgList.forEach((org) => {
      if (Array.isArray(orgId)) {
        if (orgId.includes(org._id)) {
          organizationNames.push(org.name);
        }
      } else if (typeof orgId === "string") {
        if (orgId === org._id) {
          organizationNames.push(org.name);
        }
      }
    });
    return organizationNames.length > 0 ? organizationNames.join(", ") : "-";
  };
  const getTaskName = (taskId: string[]) => {
    let taskName;
    taskList.forEach((task) => {
      if (taskId.includes(task._id)) {
        taskName = task.name;
      }
    });
    return taskName;
  };

  const getAgentName = (agentId: string[] | string) => {
    let agentName;
    agentList.forEach((agent) => {
      if (agentId.includes(agent._id)) {
        agentName = agent.name;
      }
    });
    return agentName;
  };

  function getListData(data: AuditLogT) {
    let listData;
    if (data.action === "create" && data.changeLog?.to) {
      if (data.entity === "user") {
        listData = (
          <div>
            <li>Name: {data.changeLog.to.name}</li>
            <li>Email: {data.changeLog.to.email}</li>
            <li>Role: {getRole(data.changeLog.to?.role || "")}</li>
            {data.changeLog.to.role !== "super_admin" ? (
              <li>
                Granted Access For:{" "}
                {Array.isArray(data.changeLog.to?.granted_organization) &&
                data.changeLog.to.granted_organization.length > 0 ? (
                  <ul>
                    {data.changeLog.to.granted_organization.map(
                      (
                        org: string | string[],
                        index: Key | null | undefined
                      ) => (
                        <li key={index}>{getOrgName(org)}</li>
                      )
                    )}
                  </ul>
                ) : (
                  "No organization granted"
                )}
              </li>
            ) : (
              <div> </div>
            )}
          </div>
        );
      } else if (data.entity === "organization" && data.changeLog?.to.license) {
        listData = (
          <div>
            <li>Name: {data.changeLog.to.name}</li>
            <li>
              Single Task Agents: {data.changeLog.to.license.single_task_agent}{" "}
            </li>
            <li>
              Multiple Task Agents:
              {data.changeLog.to.license.multiple_task_agent}{" "}
            </li>
          </div>
        );
      } else if (data.entity === "agent") {
        listData = (
          <div>
            <li>Agent Name: {data.changeLog.to.name}</li>
            <li>Agent Type: {data.changeLog.to.type}</li>
            <li>Agent Time Zone: {data.changeLog.to.timezone}</li>
          </div>
        );
      } else if (data.entity === "queue") {
        listData = (
          <div>
            <li>Queue Name: {data.changeLog.to.queueName}</li>
            <li>
              Organization: {getOrgName(data.changeLog.to?.organization || [])}
            </li>
          </div>
        );
      } else if (data.entity === "apiKey") {
        listData = (
          <div>
            <li>Description: {data.changeLog.to.description}</li>
            <li>Expiry Date: {data.changeLog.to.expiry_date}</li>
            <li>
              Organization: {getOrgName(data.changeLog.to?.organization || [])}
            </li>
          </div>
        );
      } else if (data.entity === "task") {
        listData = (
          <div>
            <li>Task Name: {data.changeLog.to.name}</li>
            <li>
              Organization: {getOrgName(data.changeLog.to?.organization || [])}
            </li>
          </div>
        );
      } else if (data.entity === "emailConnection") {
        listData = (
          <div>
            <li>Email Id: {data.changeLog.to.name}</li>
            <li>
              Organization:{" "}
              {getOrgName(data.changeLog.to.granted_organization || [])}
            </li>
          </div>
        );
      } else if (data.entity === "scheduler") {
        listData = (
          <div>
            {data.changeLog.to.schedulerData?.task ? (
              <li>
                Task Name :{" "}
                {getTaskName([data.changeLog.to.schedulerData.task])}
              </li>
            ) : (
              <div> </div>
            )}
            {data.changeLog.to.schedulerData?.task ? (
              <li>Version : {data.changeLog.to.schedulerData.version}</li>
            ) : (
              <div> </div>
            )}
            {data.changeLog.to.schedulerData?.organization ? (
              <li>
                Organization :{" "}
                {getOrgName(data.changeLog.to.schedulerData.organization)}
              </li>
            ) : (
              <div> </div>
            )}
            {data.changeLog.to.schedulerData?.agent ? (
              <li>
                Agent : {getAgentName(data.changeLog.to.schedulerData.agent)}
              </li>
            ) : (
              <div> </div>
            )}
            {data.changeLog.to.schedulerData?.start_date ? (
              <li>
                Start Date :{" "}
                {data.changeLog.to.schedulerData.start_date.replaceAll(
                  " ",
                  "-"
                )}
              </li>
            ) : (
              <div> </div>
            )}
            {data.changeLog.to.schedulerData?.end_date ? (
              <li>
                End Date :{" "}
                {data.changeLog.to.schedulerData.end_date.replaceAll(" ", "-")}
              </li>
            ) : (
              <div> </div>
            )}
            {data.changeLog.to.schedulerData?.recurrence ? (
              <li>Recurrence : {data.changeLog.to.schedulerData.recurrence}</li>
            ) : (
              <div> </div>
            )}
            {data.changeLog.to.schedulerData?.occurrence ? (
              <li>Ocurrence : {data.changeLog.to.schedulerData.ocurrence}</li>
            ) : (
              <div> </div>
            )}
            {data.changeLog.to.schedulerData?.repeat ? (
              <li>
                Repeat : {data.changeLog.to.schedulerData.repeat}{" "}
                {data.changeLog.to.schedulerData.repeat_interval}
              </li>
            ) : (
              <div> </div>
            )}
            {data.changeLog.to.schedulerData?.selected_days &&
            data.changeLog.to.schedulerData.selected_days.length > 0 ? (
              <li>
                Selected Days :{" "}
                {formatSelectedDays(
                  data.changeLog.to.schedulerData.selected_days
                )}
              </li>
            ) : (
              <div> </div>
            )}
            {data.changeLog.to.schedulerData?.execution_time ? (
              <li>
                Start Time :{" "}
                {formatSelectedTime(
                  data.changeLog.to.schedulerData.execution_time
                )}
              </li>
            ) : (
              <div> </div>
            )}
            {data.changeLog.to.schedulerData?.start_time ? (
              <li>
                Start Time :{" "}
                {formatSelectedTime(data.changeLog.to.schedulerData.start_time)}
              </li>
            ) : (
              <div> </div>
            )}
            {data.changeLog.to.schedulerData?.end_time ? (
              <li>
                End Time :{" "}
                {formatSelectedTime(data.changeLog.to.schedulerData.end_time)}
              </li>
            ) : (
              <div> </div>
            )}
            {data.changeLog.to.schedulerData?.selected_dates ? (
              <li>
                Selected Dates :{" "}
                {formatSelectedDays(
                  data.changeLog.to.schedulerData.selected_dates
                )}
              </li>
            ) : (
              <div> </div>
            )}
            {data.changeLog.to.schedulerData?.execution_type ? (
              <li>
                Execution Type :{" "}
                {data.changeLog.to.schedulerData.execution_type}
              </li>
            ) : (
              <div> </div>
            )}
            {data.changeLog.to.schedulerData?.scheduled_dates ? (
              <li>
                Scheduled Dates :{" "}
                {formatSelectedDays(
                  data.changeLog.to.schedulerData.scheduled_dates
                )}
              </li>
            ) : (
              <div> </div>
            )}
          </div>
        );
      }
    } else if (data.action === "update") {
      if (data.entity === "user") {
        listData = (
          <div>
            {data.changeLog?.to?.name ||
            data.changeLog?.to?.active_status !== undefined ||
            data.changeLog?.to?.granted_organization ? (
              <div>
                {data.changeLog?.to?.name && (
                  <li>Name to: {data.changeLog.to.name}</li>
                )}
                {data.changeLog?.to?.active_status !== undefined && (
                  <li>
                    User Status:{" "}
                    {getAgentStatus(data.changeLog.to.active_status)}
                  </li>
                )}
                {data.changeLog.to.addedOrganizations ? (
                  <li>
                    Added Organization:{" "}
                    {getOrgName(data.changeLog.to.addedOrganizations)}
                  </li>
                ) : (
                  <div> </div>
                )}
                {data.changeLog?.to.removedOrganizations !== "" && (
                  <li>
                    Removed Organization:{" "}
                    {getOrgName(data.changeLog.to.removedOrganizations)}
                  </li>
                )}
              </div>
            ) : (
              <li>No Updates</li>
            )}
          </div>
        );
      } else if (data.entity === "organization") {
        listData = (
          <div>
            {data.changeLog ? (
              <div>
                {data.changeLog?.to?.name ? (
                  <li>Name to: {data.changeLog.to.name}</li>
                ) : (
                  <div> </div>
                )}
                {data.changeLog?.to?.license?.single_task_agent ? (
                  <li>
                    Single Task Agents:{" "}
                    {data.changeLog.to.license.single_task_agent}
                  </li>
                ) : (
                  <div> </div>
                )}
                {data.changeLog?.to?.license?.multiple_task_agent ? (
                  <li>
                    Multiple Task Agents:
                    {data.changeLog.to.license.multiple_task_agent}
                  </li>
                ) : (
                  <div> </div>
                )}
              </div>
            ) : (
              <div>
                <li>No Updates</li>
              </div>
            )}
          </div>
        );
      } else if (data.entity === "apiKey") {
        listData = (
          <div>
            <li>Description: {data.changeLog?.to?.description}</li>
            <li>Expiry Date: {data.changeLog?.to?.expiry_date}</li>
          </div>
        );
      } else if (data.entity === "agent") {
        listData = (
          <div>
            {data.changeLog ? (
              <div>
                {data.changeLog?.to?.name ? (
                  <li>Changed Agent Name to: {data.changeLog.to.name}</li>
                ) : (
                  <div> </div>
                )}
                {data.changeLog?.to?.active_status !== undefined ? (
                  <li>
                    Status: {getAgentStatus(data.changeLog.to.active_status)}
                  </li>
                ) : (
                  <div> </div>
                )}
                {data.changeLog?.to?.imported_Task ? (
                  <>
                    <li>
                      Imported Task:{" "}
                      {getTaskName([data.changeLog.to.imported_Task])}
                    </li>
                    <li>Imported Version: {data.changeLog.to.version}</li>
                  </>
                ) : (
                  <div> </div>
                )}

                {data.changeLog?.to?.task ? (
                  <>
                    <li>Task: {getTaskName(data.changeLog.to.task)}</li>
                    {data.changeLog?.to?.version ? (
                      <li>To Version: {data.changeLog.to.version}</li>
                    ) : (
                      <div> </div>
                    )}
                    {data.changeLog?.to?.config ? (
                      <li>Updated the config details</li>
                    ) : (
                      <div> </div>
                    )}
                  </>
                ) : (
                  <div> </div>
                )}
              </div>
            ) : (
              <div>
                <li>No Updates</li>
              </div>
            )}
          </div>
        );
      } else if (data.entity === "emailConnection") {
        listData = (
          <div>
            <li>Email Id: {data?.changeLog?.to?.name}</li>
            <li>
              Organization:{" "}
              {getOrgName(data?.changeLog?.to?.granted_organization || [])}
            </li>
          </div>
        );
      } else if (data.entity === "scheduler") {
        listData = (
          <div>
            {"changeLog" in data ? (
              <div>
                {data.changeLog?.to?.taskId ? (
                  <>
                    <li>
                      Task Name : {getTaskName([data.changeLog.to.taskId])}
                    </li>
                    <li>Version: {data.changeLog.to.version}</li>
                  </>
                ) : (
                  <div> </div>
                )}
                {data.changeLog?.to?.start_date ? (
                  <li>
                    Start Date :{" "}
                    {data.changeLog.to.start_date.replaceAll(" ", "-")}
                  </li>
                ) : (
                  <div> </div>
                )}
                {data.changeLog?.to?.end_date ? (
                  <li>
                    End Date : {data.changeLog.to.end_date.replaceAll(" ", "-")}
                  </li>
                ) : (
                  <div> </div>
                )}
                {data.changeLog?.to?.recurrence ? (
                  <li>Recurrence : {data.changeLog.to.recurrence}</li>
                ) : (
                  <div> </div>
                )}
                {data.changeLog?.to?.ocurrence ? (
                  <li>Ocurrence : {data.changeLog.to.ocurrence}</li>
                ) : (
                  <div> </div>
                )}
                {data.changeLog?.to?.repeat ? (
                  <li>
                    Repeat : {data.changeLog.to.repeat}{" "}
                    {data.changeLog.to.repeat_interval}
                  </li>
                ) : (
                  <div> </div>
                )}
                {data.changeLog?.to?.selected_days ? (
                  <li>
                    Selected Days :{" "}
                    {formatSelectedDays(data.changeLog.to.selected_days)}
                  </li>
                ) : (
                  <div> </div>
                )}
                {data.changeLog?.to?.execution_time ? (
                  <li>
                    Start Time :{" "}
                    {formatSelectedTime(data.changeLog.to.execution_time)}
                  </li>
                ) : (
                  <div> </div>
                )}
                {data.changeLog?.to?.start_time ? (
                  <li>
                    Start Time :{" "}
                    {formatSelectedTime(data.changeLog.to.start_time)}
                  </li>
                ) : (
                  <div> </div>
                )}
                {data.changeLog?.to?.end_time ? (
                  <li>
                    End Time : {formatSelectedTime(data.changeLog.to.end_time)}
                  </li>
                ) : (
                  <div> </div>
                )}
                {data.changeLog?.to?.selected_dates ? (
                  <li>
                    Selected Dates :{" "}
                    {formatSelectedDays(data.changeLog.to.selected_dates)}
                  </li>
                ) : (
                  <div> </div>
                )}
                {data.changeLog?.to?.scheduled_dates ? (
                  <li>
                    Scheduled Dates :{" "}
                    {formatSelectedDays([data.changeLog.to.scheduled_dates])}
                  </li>
                ) : (
                  <div> </div>
                )}
              </div>
            ) : (
              <div>
                <li>No Updates</li>
              </div>
            )}
          </div>
        );
      }
    } else if (data.action === "delete") {
      if (data.entity === "emailConnection") {
        listData = (
          <div>
            <li>Email Id: {data.changeLog?.to?.name}</li>
            <li>
              Organization:{" "}
              {getOrgName(data.changeLog?.to?.granted_organization || [])}
            </li>
          </div>
        );
      } else if (data.entity === "user") {
        listData = (
          <div>
            <li>User Name: {data.changeLog?.to?.name}</li>
            <li>Role: {getRole(data.changeLog?.to?.role || "")}</li>
          </div>
        );
      } else if (data.entity === "agent") {
        listData = (
          <div>
            {data.changeLog?.to?.deletedTask ? (
              <>
                Deleted the below Task:
                <li>Task: {getTaskName([data?.changeLog?.to?.deletedTask])}</li>
                <li>Version: {[data?.changeLog?.to?.deletedVersion]}</li>
              </>
            ) : (
              <div> </div>
            )}
            {data.changeLog?.to?.name ? (
              <>
                <li>Agent Name: {data.changeLog?.to?.name}</li>
                <li>
                  Organization:{" "}
                  {getOrgName(data.changeLog?.to?.granted_organization || [])}
                </li>
              </>
            ) : (
              <div> </div>
            )}
          </div>
        );
      } else if (data.entity === "apiKey") {
        listData = (
          <div>
            <li>Description: {data.changeLog?.to?.description}</li>
            <li>
              Organization:{" "}
              {getOrgName(data.changeLog?.to?.granted_organization || [])}
            </li>
          </div>
        );
      } else if (data.entity === "organization") {
        listData = (
          <div>
            <li>Org Name: {getOrgName(data.organization)}</li>
          </div>
        );
      } else if (data.entity === "scheduler") {
        listData = (
          <div>
            {data.changeLog?.to?.deletedTask ? (
              <>
                Deleted the schedule of Task:
                <li>Task: {getTaskName([data?.changeLog?.to?.deletedTask])}</li>
                <li>Version: {[data?.changeLog?.to?.deletedVersion]}</li>
              </>
            ) : (
              <div> </div>
            )}
          </div>
        );
      }
    }
    return listData;
  }

  const getSentenceData = (data: AuditLogT) => {
    let sentenceData;
    if (data.action === "other") {
      if (data.logging === "Login")
        sentenceData = `${getRole(data.actionedBy.role)} has Logged in`;
      if (data.logging === "Deployed") {
        sentenceData = `${getAgentName(data.entityId)} Agent is Deployed`;
      }
      if (data.logging === "Undeployed") {
        sentenceData = `${getAgentName(data.entityId)} Agent is UnDeployed`;
      }
    }
    if (data.action === "create") {
      sentenceData = `${data.actionedBy.name} has created new ${
        data.entity === "user" && data.changeLog?.to?.role
          ? getRole(data.changeLog.to.role)
          : getRole(data.entity)
      } with following details`;
    }
    if (data.action === "update") {
      sentenceData = `${
        data.actionedBy.name
      } has updated following details of ${getRole(data.entity)}`;
    }
    if (data.action === "delete") {
      sentenceData = `${data.actionedBy.name} has deleted ${getRole(
        data.entity
      )} with following details`;
    }
    return sentenceData;
  };

  const calendarChangeHandler = (value: DateObject | DateObject[] | null) => {
    if (value && Array.isArray(value)) {
      setScheduleDate(
        value.map((calenderItem: DateObject) => {
          return `${calenderItem.year}-${calenderItem.month.number}-${calenderItem.day}`;
        })
      );
    }
  };

  const handleOptionChange = (
    event: ChangeEvent<HTMLInputElement>,
    setOption: React.Dispatch<React.SetStateAction<string[]>>,
    option: string[]
  ) => {
    if (event.target.checked) {
      setOption(option.concat(event.target.value));
    } else {
      setOption(option.filter((item) => item !== event.target.value));
    }
  };
  const handleOrgOption = (event: ChangeEvent<HTMLInputElement>) =>
    handleOptionChange(event, setOrgOption, orgOption);
  const handleUserOption = (event: ChangeEvent<HTMLInputElement>) =>
    handleOptionChange(event, setUserOption, userOption);
  const handleEntityOption = (event: ChangeEvent<HTMLInputElement>) =>
    handleOptionChange(event, setEntityOption, entityOption);
  const handleActionOption = (event: ChangeEvent<HTMLInputElement>) =>
    handleOptionChange(event, setActionOption, actionOption);

  let requestBody: Record<string, object> = {};

  function filterRequestBody() {
    requestBody = {};
    if (orgOption.length > 0) {
      Object.assign(requestBody, { organization: { $in: orgOption } });
    }
    if (userOption.length > 0) {
      Object.assign(requestBody, { "actionedBy.name": { $in: userOption } });
    }
    if (entityOption.length > 0) {
      Object.assign(requestBody, { entity: { $in: entityOption } });
    }
    if (actionOption.length > 0) {
      Object.assign(requestBody, { action: { $in: actionOption } });
    }
    if (scheduleDate.length === 2) {
      const startDate = scheduleDate[0];
      const endDate = scheduleDate[1];
      Object.assign(requestBody, {
        createdAt: { $gte: startDate, $lt: endDate },
      });
    }
    return requestBody;
  }

  useEffect(() => {
    const reqData = filterRequestBody();
    setError(false);
    if (Object.keys(reqData).length !== 0) {
      auditFilterLogs(authToken, reqData)
        .then((res) => {
          if (res.data && res.data.length > 0) {
            setFilterResult(res.data);
            setHasMore(res.data.length > 0);
          } else {
            setFilterResult([]);
          }
        })
        .catch(() => {
          setError(true);
        });
    } else {
      setFilterResult([]);
    }
  }, [
    actionOption,
    auditData,
    authToken,
    entityOption,
    orgOption,
    scheduleDate,
    userOption,
  ]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      setLoading(true);
      setError(false);

      try {
        const [
          auditResult,
          userListResult,
          organizationList,
          getTaskList,
          getAgentList,
        ] = await Promise.all([
          auditTaskLogs(authToken, pageNumber),
          allUserList(authToken),
          orgListApi(authToken),
          taskListApi(authToken),
          agentListApi(authToken),
        ]);
        const organizationIds = organizationList?.data.map(
          (org: { _id: any }) => org._id
        );

        const filteredUsers = userListResult?.data.filter((user) => {
          return user.granted_organization.some((orgId) =>
            organizationIds.includes(orgId)
          );
        });

        const flaggedOrganizations = organizationList.data.map((org: any) => {
          return {
            _id: org._id,
            name: org.name,
            flag: org.deleted_status ? "🚩" : "",
          };
        });

        setAuditData((prevData) => [...prevData, ...auditResult?.data]);
        setOrgList(flaggedOrganizations);
        setUserListItem(filteredUsers);
        setTaskList(getTaskList?.data || []);
        setAgentList(getAgentList.data);
        setHasMore(auditResult.data.length > 0);
        setLoading(false);
        setLoadingMore(false);
      } catch (errorany) {
        setError(true);
        setLoading(false);
        setLoadingMore(false);
      }
    };

    fetchUserDetails();
  }, [authToken, pageNumber]);

  const dataToDisplay = filterResult.length > 0 ? filterResult : auditData;

  const downloadExcel = useCallback(async () => {
    const request = filterRequestBody();

    if (Object.keys(request).length === 0) {
      Object.assign(request, { entity: { $in: entityList } });
    }

    const response = await auditFilterLogs(authToken, request);
    const datas = response.data;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Audit Logs");

    worksheet.columns = [
      { header: "S.No", key: "sno", width: 10 },
      { header: "Date & Time", key: "date", width: 20 },
      { header: "Entity Type", key: "entity", width: 15 },
      { header: "Action", key: "action", width: 15 },
      { header: "Performed By", key: "performedBy", width: 20 },
      { header: "Role", key: "role", width: 20 },
      { header: "Organization", key: "organization", width: 20 },
      { header: "Details", key: "details", width: 30 },
    ];

    function extractTextFromJSX(element: any): string {
      if (typeof element === "string") {
        return element;
      }
      if (Array.isArray(element)) {
        return element.map(extractTextFromJSX).join(" ");
      }
      if (element?.props?.children) {
        return extractTextFromJSX(element.props.children);
      }
      return "";
    }

    datas.forEach((data: any, index: any) => {
      const sentenceData = getSentenceData(data) || "";
      const listData = getListData(data) || "";
      const listString = extractTextFromJSX(listData);
      const combinedDetails = `${sentenceData}${listString}`.trim();
      worksheet.addRow({
        sno: index + 1,
        date: `${dateFormatter(data.createdAt)} at ${timeFormatter(
          data.createdAt
        )}`,
        entity: data.entity.charAt(0).toUpperCase() + data.entity.slice(1),
        action: data.action.toUpperCase(),
        performedBy: data.actionedBy?.name || data.changeLog?.to?.name,
        role: data.actionedBy?.role ? getRole(data.actionedBy?.role) : "-",
        organization: data.organization ? getOrgName(data.organization) : "-",
        details: combinedDetails,
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "audit_logs.xlsx");
  }, [getListData, getOrgName, getSentenceData]);

  const downloadRequest = useCallback(async () => {
    const request = filterRequestBody();
    if (Object.keys(request).length !== 0) {
      setShowModal(true);
    } else {
      await downloadExcel();
    }
  }, [downloadExcel, filterRequestBody]);

  const handleDownloadConfirm = async () => {
    setShowModal(false);
    await downloadExcel();
  };

  const handleDownloadCancel = () => {
    setShowModal(false);
  };

  return (
    <>
      <div className="containerBox">
        {loading && pageNumber === 1 ? (
          <Loader />
        ) : (
          <>
            <div className="header">
              <Col md="8" lg="8" className="botton-col">
                <div className="spacing-1-1">
                  <div className="pad-button card-title">Audit Logs</div>
                  <FilterDropdown
                    label="Organization"
                    items={orgList}
                    handleOptionChange={handleOrgOption}
                  />
                  {userListItem.length > 0 && (
                    <div className="pad-button">
                      <Dropdown>
                        <Dropdown.Toggle className="dropdown-org buttons text">
                          User 𝅍
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="dropdown-menu-space navbar-collapse">
                          {userListItem.map((userData) => {
                            return (
                              <label
                                key={userData._id}
                                className="dropdown-option org-item"
                                htmlFor={userData._id}
                              >
                                <input
                                  type="checkbox"
                                  value={userData.name}
                                  data-value={userData.name}
                                  onChange={handleUserOption}
                                  id={userData._id}
                                  className="check-box"
                                />{" "}
                                {getUserRole(userData)}
                              </label>
                            );
                          })}
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  )}
                  <FilterDropdown
                    label="Entity"
                    items={entityList}
                    handleOptionChange={handleEntityOption}
                  />
                  <FilterDropdown
                    label="Action"
                    items={actionList}
                    handleOptionChange={handleActionOption}
                  />

                  <div className="pad-button">
                    <InputGroup>
                      <Form.Control
                        className="radioButtonLabel"
                        placeholder="Select Start Date and End Date"
                        value={scheduleDate.map((item) => item) || ""}
                        onChange={() => setScheduleDate([])}
                        style={{ width: "170px", padding: "12px" }}
                      />
                      <InputGroup.Text
                        id="basic-addon2"
                        onClick={() => {
                          setShowCalender(!showCalender);
                        }}
                      >
                        <img src={CalenderIcon} alt="CalendarIcon" />
                      </InputGroup.Text>
                    </InputGroup>
                    {showCalender && (
                      <div style={{ position: "absolute", zIndex: "1" }}>
                        <Calendar
                          range
                          value={scheduleDate.map((item) => item) || ""}
                          format="YYYY-MM-DD"
                          onChange={calendarChangeHandler}
                        />
                      </div>
                    )}
                  </div>

                  <div onClick={downloadRequest} role="button" tabIndex={0}>
                    <img
                      src={download}
                      alt="download"
                      style={{ paddingLeft: "5px", width: "48px" }}
                    />
                  </div>
                </div>
              </Col>
              <Col className="search-col">
                <div style={{ fontSize: "5px", marginTop: "20px" }}>
                  <p>Note: 🚩 incicates the item is deleted</p>
                </div>
              </Col>
            </div>
            <hr className="divider-line1" />
            <Row className="spacing-1">
              {dataToDisplay.length === 0 ? (
                <NoDataFound message1="No Audit logs found" message2="" />
              ) : (
                <Table className="table" borderless>
                  <thead className="table-head">
                    <tr className="table-row no-after">
                      <th style={{ width: "1%" }}>&nbsp;</th>
                      <th style={{ width: "5%" }}>S.No</th>
                      <th style={{ width: "15%" }}>Date & Time</th>
                      <th style={{ width: "10%" }}>Entity Type</th>
                      <th style={{ width: "10%" }}>Action</th>
                      <th style={{ width: "10%" }}>Performed By</th>
                      <th style={{ width: "10%" }}>Role</th>
                      <th style={{ width: "10%" }}>Organization</th>
                      <th style={{ width: "45%" }}>Details</th>
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {dataToDisplay.sort(sortByNewest).map((data, index) => {
                      const isLastElement = index === dataToDisplay.length - 1;

                      return (
                        <tr
                          key={data._id}
                          ref={isLastElement ? lastLogElementRef : null}
                        >
                          <td />
                          <td className="body-tr iconDetail">{index + 1}</td>
                          <td
                            style={{ fontSize: "12px", paddingTop: "12px" }}
                          >{`${dateFormatter(
                            data.createdAt
                          )} at ${timeFormatter(data.createdAt)}`}</td>

                          <td style={{ fontSize: "12px", paddingTop: "12px" }}>
                            {data.entity.charAt(0).toUpperCase() +
                              data.entity.slice(1)}
                          </td>
                          <td
                            style={{
                              fontSize: "12px",
                              paddingTop: "12px",
                            }}
                          >
                            <span style={getChipStyle(data.action)}>
                              {data.action.toUpperCase()}
                            </span>
                          </td>
                          <td style={{ fontSize: "12px", paddingTop: "12px" }}>
                            {data.actionedBy?.name || data.changeLog?.to?.name}
                          </td>
                          <td style={{ fontSize: "12px", paddingTop: "12px" }}>
                            {data.actionedBy?.role
                              ? getRole(data.actionedBy.role)
                              : "-"}
                          </td>
                          <td style={{ fontSize: "12px", paddingTop: "12px" }}>
                            {data.organization
                              ? getOrgName(data.organization)
                              : "-"}
                          </td>
                          <td className="body-tr iconDetail">
                            {getSentenceData(data)}
                            <div style={{ paddingLeft: "10px" }}>
                              {getListData(data)}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {error && (
                      <tr>
                        <td> Error</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </Row>
            {loadingMore && !filterResult.length && <Loader />}
            <FilterWarningModal
              show={showModal}
              handleConfirm={handleDownloadConfirm}
              handleCancel={handleDownloadCancel}
            />
          </>
        )}
      </div>
    </>
  );
}

export default AuditLogs;
